// src/pages/processifyd/ProcessifydRACI.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions"; 
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faFolderOpen,
  faSearch,
  faTimes,
  faChevronLeft,
  faChevronRight,
  faUsers,
  faUser,
} from "@fortawesome/free-solid-svg-icons";

const ProcessifydRACI = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("groups"); // "groups" or "persons"

  // Data States
  const [raciGroups, setRaciGroups] = useState([]);
  const [raciPersons, setRaciPersons] = useState([]);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [modalType, setModalType] = useState("group"); // "group" or "person"

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  // Initial Forms
  const initialGroupForm = {
    raciGroupName: "",
    aboutTheRaciGroup: "",
    active: "Yes",
    archive: "No",
  };

  const initialPersonForm = {
    raciPersonName: "",
    aboutTheRaciPerson: "",
    raciPersonGroup: "",
    active: "Yes",
    deleteUndelete: "No",
  };

  const [formData, setFormData] = useState(initialGroupForm);

  // Sample Data
  const demoRaciGroups = [
    {
      id: 1,
      raciGroupName: "Relevant Teams",
      aboutTheRaciGroup: "Relevant Teams",
      active: "Yes",
      archive: "Yes",
      createdBy: "Sanjeev Kumar",
      createdDate: "12/12/23 15:32",
      lastUpdatedBy: "Sanjeev Kumar",
      lastUpdateDate: "12/12/23 15:32",
    },
  ];

  const demoRaciPersons = [
    {
      id: 101,
      raciPersonName: "Sanjeev Kumar",
      aboutTheRaciPerson: "Process Analyst",
      raciPersonGroup: "Analyst Team",
      active: "Yes",
      deleteUndelete: "Yes",
      createdBy: "Sanjeev Kumar",
      createdDate: "12/12/23 15:32",
      lastUpdatedBy: "Sanjeev Kumar",
      lastUpdateDate: "12/12/23 15:32",
    },
  ];

  const loadSample = () => {
    if (activeTab === "groups") {
      setRaciGroups(demoRaciGroups);
    } else {
      setRaciPersons(demoRaciPersons);
    }
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setModalType(activeTab === "groups" ? "group" : "person");
    setFormData(activeTab === "groups" ? initialGroupForm : initialPersonForm);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setModalType(activeTab === "groups" ? "group" : "person");
    
    setFormData(activeTab === "groups" 
      ? {
          raciGroupName: item.raciGroupName || "",
          aboutTheRaciGroup: item.aboutTheRaciGroup || "",
          active: item.active || "Yes",
          archive: item.archive || "No",
        }
      : {
          raciPersonName: item.raciPersonName || "",
          aboutTheRaciPerson: item.aboutTheRaciPerson || "",
          raciPersonGroup: item.raciPersonGroup || "",
          active: item.active || "Yes",
          deleteUndelete: item.deleteUndelete || "No",
        }
    );
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm(`Delete this ${activeTab === "groups" ? "RACI Group" : "RACI Person"}?`)) return;
    
    if (activeTab === "groups") {
      setRaciGroups(prev => prev.filter(g => g.id !== id));
    } else {
      setRaciPersons(prev => prev.filter(p => p.id !== id));
    }
  };

  const handleOpen = (item) => {
    navigate(activeTab === "groups" 
      ? `/processifyd-racigroup/${item.id}` 
      : `/processifyd-raciperson/${item.id}`
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (modalType === "group") {
      if (!formData.raciGroupName.trim()) {
        alert("RACI Group Name is required.");
        return;
      }
      const newGroup = {
        id: editingItem?.id || Date.now(),
        ...formData,
        createdBy: editingItem?.createdBy || "Sanjeev Kumar",
        createdDate: editingItem?.createdDate || new Date().toLocaleString("en-IN"),
        lastUpdatedBy: "Sanjeev Kumar",
        lastUpdateDate: new Date().toLocaleString("en-IN"),
      };

      setRaciGroups(prev => 
        editingItem 
          ? prev.map(g => g.id === editingItem.id ? newGroup : g)
          : [...prev, newGroup]
      );
    } else {
      if (!formData.raciPersonName.trim() || !formData.raciPersonGroup.trim()) {
        alert("RACI Person Name and Group are required.");
        return;
      }
      const newPerson = {
        id: editingItem?.id || Date.now(),
        ...formData,
        createdBy: editingItem?.createdBy || "Sanjeev Kumar",
        createdDate: editingItem?.createdDate || new Date().toLocaleString("en-IN"),
        lastUpdatedBy: "Sanjeev Kumar",
        lastUpdateDate: new Date().toLocaleString("en-IN"),
      };

      setRaciPersons(prev => 
        editingItem 
          ? prev.map(p => p.id === editingItem.id ? newPerson : p)
          : [...prev, newPerson]
      );
    }

    setShowModal(false);
    setFormData(modalType === "group" ? initialGroupForm : initialPersonForm);
  };

  // Filter & Pagination Logic
  const currentData = activeTab === "groups" ? raciGroups : raciPersons;

  const filtered = currentData.filter(item => {
    const term = search.toLowerCase();
    if (activeTab === "groups") {
      return (
        item.raciGroupName?.toLowerCase().includes(term) ||
        item.aboutTheRaciGroup?.toLowerCase().includes(term)
      );
    } else {
      return (
        item.raciPersonName?.toLowerCase().includes(term) ||
        item.raciPersonGroup?.toLowerCase().includes(term) ||
        item.aboutTheRaciPerson?.toLowerCase().includes(term)
      );
    }
  });

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4 md:p-6">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black border-b border-blue-500/40 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-cyan-300">
            👤 RACI Matrix
          </div>
          <div className="text-xs text-gray-400">
            Responsible, Accountable, Consulted, Informed
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {isLoggedIn && (
            <>
              <span className="text-xs text-teal-300">{userName}</span>
              <button
                onClick={handleLogout}
                className="w-12 px-2 py-1 bg-red-700/80 hover:bg-red-600 rounded-md text-sm"
                title="Logout"
              >
                ⏻
              </button>
            </>
          )}
        </div>

        <button
          className="md:hidden text-2xl text-cyan-300"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-4">
          <div className="flex flex-col gap-4">
            <button
              onClick={() => { setActiveTab("groups"); setCurrentPage(1); setSearch(""); setIsMobileMenuOpen(false); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${activeTab === "groups" ? "bg-cyan-600 text-white" : "text-gray-400"}`}
            >
              <FontAwesomeIcon icon={faUsers} /> RACI Groups
            </button>
            <button
              onClick={() => { setActiveTab("persons"); setCurrentPage(1); setSearch(""); setIsMobileMenuOpen(false); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${activeTab === "persons" ? "bg-cyan-600 text-white" : "text-gray-400"}`}
            >
              <FontAwesomeIcon icon={faUser} /> RACI Persons
            </button>
          </div>
        </div>
      )}

      <div className="min-h-[75vh]">
        {/* Tabs */}
        <div className="mt-6 flex border-b border-gray-700">
          <button
            onClick={() => { setActiveTab("groups"); setCurrentPage(1); setSearch(""); }}
            className={`px-8 py-3 font-medium flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === "groups" 
                ? "border-cyan-400 text-cyan-300" 
                : "border-transparent text-gray-400 hover:text-gray-200"
            }`}
          >
            <FontAwesomeIcon icon={faUsers} /> RACI Groups
          </button>
          <button
            onClick={() => { setActiveTab("persons"); setCurrentPage(1); setSearch(""); }}
            className={`px-8 py-3 font-medium flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === "persons" 
                ? "border-cyan-400 text-cyan-300" 
                : "border-transparent text-gray-400 hover:text-gray-200"
            }`}
          >
            <FontAwesomeIcon icon={faUser} /> RACI Persons
          </button>
        </div>

        {/* Controls */}
        <div className="mt-4 flex flex-col lg:flex-row gap-4">
          <div className="flex gap-3">
            <button
              onClick={loadSample}
              className="flex items-center gap-2 px-6 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
            >
              <FontAwesomeIcon icon={faPlus} /> Load Sample
            </button>
            <button
              onClick={handleAddNew}
              className="flex items-center gap-2 px-6 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
            >
              <FontAwesomeIcon icon={faPlus} /> 
              New {activeTab === "groups" ? "RACI Group" : "RACI Person"}
            </button>
          </div>

          <div className="relative w-full">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />
            <input
              type="text"
              placeholder={`Search ${activeTab === "groups" ? "RACI Group" : "RACI Person"}...`}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-cyan-500/60"
            />
          </div>
        </div>

        {/* Cards */}
        <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-3 xl:grid-cols-4">
          {paginated.map((item) => (
            <div
              key={item.id}
              className="bg-gray-800 border border-gray-700 rounded-xl p-5 hover:border-cyan-600/50 transition-colors group"
            >
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-base text-white group-hover:text-cyan-300 transition-colors">
                    {activeTab === "groups" ? item.raciGroupName : item.raciPersonName}
                  </h3>
                  {activeTab === "persons" && item.raciPersonGroup && (
                    <p className="text-sm text-cyan-300 mt-1">{item.raciPersonGroup}</p>
                  )}
                </div>

                <div className="flex flex-col items-end gap-1">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${item.active === "Yes" ? "bg-emerald-600" : "bg-red-600"}`}>
                    {item.active === "Yes" ? "Active" : "Inactive"}
                  </span>
                  {(activeTab === "groups" && item.archive === "Yes") && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-600">Archived</span>
                  )}
                  {(activeTab === "persons" && item.deleteUndelete === "Yes") && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-rose-600">Deleted</span>
                  )}
                </div>
              </div>

              <p className="mt-4 text-sm text-gray-300 line-clamp-3">
                {activeTab === "groups" 
                  ? item.aboutTheRaciGroup 
                  : item.aboutTheRaciPerson || "No description available"}
              </p>

              <div className="mt-6 flex gap-2">
                <button
                  onClick={() => handleOpen(item)}
                  className="flex-1 py-2 bg-gradient-to-r from-cyan-700 to-teal-700 hover:from-cyan-600 hover:to-teal-600 rounded-lg font-medium flex items-center justify-center gap-2 text-sm"
                >
                  <FontAwesomeIcon icon={faFolderOpen} /> Open
                </button>
                <button
                  onClick={() => handleEdit(item)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  title="Edit"
                >
                  <FontAwesomeIcon icon={faEdit} className="text-yellow-400" />
                </button>
                <button
                  onClick={() => handleDelete(item.id)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  title="Delete"
                >
                  <FontAwesomeIcon icon={faTrash} className="text-red-400" />
                </button>
              </div>
            </div>
          ))}

          {paginated.length === 0 && (
            <div className="col-span-full py-12 text-center text-gray-500">
              {search 
                ? "No records match your search" 
                : `No ${activeTab === "groups" ? "RACI Groups" : "RACI Persons"} yet. Click "New" or "Load Sample"`}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-10">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="w-10 px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="text-sm">
              Page <strong>{currentPage}</strong> of {totalPages}
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="w-10 px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-lg border border-gray-700 max-h-[92vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-cyan-300">
                  {editingItem ? "Edit" : "New"} {modalType === "group" ? "RACI Group" : "RACI Person"}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-white text-2xl"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Modal Form Fields - Same as before */}
                {modalType === "group" ? (
                  /* Group Form Fields */
                  <>
                    <div>
                      <label className="block text-sm mb-1.5">RACI Group Name <span className="text-red-400">*</span></label>
                      <input
                        required
                        value={formData.raciGroupName}
                        onChange={(e) => setFormData({ ...formData, raciGroupName: e.target.value })}
                        className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                        placeholder="e.g. Relevant Teams"
                      />
                    </div>
                    <div>
                      <label className="block text-sm mb-1.5">About the RACI Group</label>
                      <textarea
                        value={formData.aboutTheRaciGroup}
                        onChange={(e) => setFormData({ ...formData, aboutTheRaciGroup: e.target.value })}
                        rows={4}
                        className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                      />
                    </div>
                  </>
                ) : (
                  /* Person Form Fields */
                  <>
                    <div>
                      <label className="block text-sm mb-1.5">RACI Person Name <span className="text-red-400">*</span></label>
                      <input
                        required
                        value={formData.raciPersonName}
                        onChange={(e) => setFormData({ ...formData, raciPersonName: e.target.value })}
                        className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                        placeholder="e.g. Sanjeev Kumar"
                      />
                    </div>
                    <div>
                      <label className="block text-sm mb-1.5">About the RACI Person</label>
                      <textarea
                        value={formData.aboutTheRaciPerson}
                        onChange={(e) => setFormData({ ...formData, aboutTheRaciPerson: e.target.value })}
                        rows={3}
                        className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm mb-1.5">RACI Person Group <span className="text-red-400">*</span></label>
                      <input
                        required
                        value={formData.raciPersonGroup}
                        onChange={(e) => setFormData({ ...formData, raciPersonGroup: e.target.value })}
                        className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                        placeholder="e.g. Analyst Team"
                      />
                    </div>
                  </>
                )}

                {/* Common Active & Archive/Delete Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1.5">Active <span className="text-red-400">*</span></label>
                    <select
                      value={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.value })}
                      className="w-full h-12 px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg"
                    >
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm mb-1.5">
                      {modalType === "group" ? "Archive / Archived" : "Delete / Undelete"}
                    </label>
                    <select
                      value={modalType === "group" ? formData.archive : formData.deleteUndelete}
                      onChange={(e) => 
                        setFormData({ 
                          ...formData, 
                          [modalType === "group" ? "archive" : "deleteUndelete"]: e.target.value 
                        })
                      }
                      className="w-full h-12 px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg"
                    >
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-8">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-8 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
                  >
                    {editingItem ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-black border-t border-blue-400 text-white py-4 mt-8">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <a href="/about-us" className="hover:text-teal-300">About</a>
            <a href="/privacy-policy" className="hover:text-teal-300">Privacy Policy</a>
            <a href="/terms-of-use" className="hover:text-teal-300">Terms of Use</a>
            <a href="/contact-us" className="hover:text-teal-300">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProcessifydRACI;